import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider, useAuth } from "./auth/AuthContext";
import { ToastProvider } from "./components/common/Toast";
import { OrganizationProvider } from "./state/organization";
import { LoadingState } from "./components/common/LoadingState";
import { CalcProjectLayout } from "./components/calculations/CalcProjectContext";
import { LandingPage } from "./components/landing/LandingPage";
import { PublicLoginPage } from "./components/landing/PublicLoginPage";
import { BetaSignupPage } from "./components/landing/BetaSignupPage";
import { AccountSetupPage } from "./components/landing/AccountSetupPage";
import { ForgotPasswordPage } from "./components/landing/ForgotPasswordPage";
import { ResetPasswordPage } from "./components/landing/ResetPasswordPage";

// Public deep pages (marketing hub). Code-split so the home landing still paints
// without pulling them; they share PublicLayout/Header/Footer with the home.
const DesignEnginePage = lazy(() => import("./components/landing/DesignEnginePage").then((m) => ({ default: m.DesignEnginePage })));
const EngineeringPage = lazy(() => import("./components/landing/EngineeringPage").then((m) => ({ default: m.EngineeringPage })));
const SecurityPage = lazy(() => import("./components/landing/SecurityPage").then((m) => ({ default: m.SecurityPage })));
const LearnPage = lazy(() => import("./components/landing/LearnPage").then((m) => ({ default: m.LearnPage })));
const LearnArticlePage = lazy(() => import("./components/landing/LearnArticlePage").then((m) => ({ default: m.LearnArticlePage })));
const PublicHelpPage = lazy(() => import("./components/help/PublicHelpPage").then((m) => ({ default: m.PublicHelpPage })));
const PrivacyPage = lazy(() => import("./components/landing/LegalPages").then((m) => ({ default: m.PrivacyPage })));
const TermsPage = lazy(() => import("./components/landing/LegalPages").then((m) => ({ default: m.TermsPage })));
const SendPlanPage = lazy(() => import("./components/landing/SendPlanPage").then((m) => ({ default: m.SendPlanPage })));

// The authenticated app is code-split out of the public bundle: anonymous
// visitors to /, /login, /beta shouldn't download the whole app graph before
// the landing paints. Each lazy() resolves to its own chunk, fetched on the
// first /app navigation and cached after. v7_startTransition keeps the current
// view on screen while the next chunk loads, so in-app navigation doesn't flash.
const AppShell = lazy(() => import("./components/shell/AppShell").then((m) => ({ default: m.AppShell })));
const ProjectDashboard = lazy(() => import("./components/dashboard/ProjectDashboard").then((m) => ({ default: m.ProjectDashboard })));
const ProjectDetail = lazy(() => import("./components/project/ProjectDetail").then((m) => ({ default: m.ProjectDetail })));
const ProjectCreate = lazy(() => import("./components/project/ProjectCreate").then((m) => ({ default: m.ProjectCreate })));
// CAD review: the embedded Design Harness replacing the DXF Viewer. Lazily
// loaded and NEVER statically imported — the CAD workspace is a ~3MB chunk and
// must not land in the main bundle. URL-only for now; nothing links here yet.
const CadReviewPage = lazy(() => import("./components/cad/CadReviewPage").then((m) => ({ default: m.CadReviewPage })));
const CadReviewPreview = import.meta.env.DEV
  ? lazy(() => import("./components/cad/CadReviewPreview").then((m) => ({ default: m.CadReviewPreview })))
  : null;
const CalcDashboard = lazy(() => import("./components/calculations/CalcDashboard").then((m) => ({ default: m.CalcDashboard })));
const CalcCreate = lazy(() => import("./components/calculations/CalcCreate").then((m) => ({ default: m.CalcCreate })));
// The Calc Viewer is a calc project's LANDING surface (overview + member
// schedule); the Add-Calculation catalog is the gallery; the two-pane NDS
// workspace is the only per-member calc-detail surface.
// Every route within one calc project nests under CalcProjectLayout. That keeps
// the project header and data owner mounted while only the working surface
// changes (viewer / add / member / schema / report details).
const CalcViewerShell = lazy(() => import("./components/calculations/CalcShellSurfaces").then((m) => ({ default: m.CalcViewerShell })));
const AddCalculationShell = lazy(() => import("./components/calculations/CalcShellSurfaces").then((m) => ({ default: m.AddCalculationShell })));
const NdsCalcWorkspace = lazy(() => import("./components/calculations/NdsCalcWorkspace").then((m) => ({ default: m.NdsCalcWorkspace })));
const CalcReportDetails = lazy(() => import("./components/calculations/CalcReportDetails").then((m) => ({ default: m.CalcReportDetails })));
// Schema-driven sheets (connections): the registry inputSchema IS the form —
// no DesignInputs translation; same persisted PUT + engine verdict.
const SchemaCalcSheet = lazy(() => import("./components/calculations/SchemaCalcSheet").then((m) => ({ default: m.SchemaCalcSheet })));
const LoadCalcWorkspace = lazy(() => import("./components/calculations/LoadCalcWorkspace").then((m) => ({ default: m.LoadCalcWorkspace })));
const CalcExportRenderPage = lazy(() => import("./components/calculations/export/CalcExportRenderPage").then((m) => ({ default: m.CalcExportRenderPage })));
// Dev-only visual-QA harness for the calc surfaces (hardcoded mock data, no
// auth/api). The lazy() ref AND its route are both inside `import.meta.env.DEV`
// guards; because DEV is a compile-time constant statically replaced with
// `false` in a production build, Vite dead-code-eliminates this dynamic import
// and never emits the harness chunk — so it's NEVER in the prod bundle.
const CalcPreviewHarness = import.meta.env.DEV
  ? lazy(() => import("./components/calculations/CalcPreviewHarness").then((m) => ({ default: m.CalcPreviewHarness })))
  : null;
const CalcExportPreviewPage = import.meta.env.DEV
  ? lazy(() => import("./components/calculations/export/CalcExportPreviewPage").then((m) => ({ default: m.CalcExportPreviewPage })))
  : null;
// Dev-only visual-QA harness for the portal AppShell CHROME (mock auth/org, no
// api). Same DEV-guard discipline as CalcPreviewHarness above: the lazy() ref
// and its route are both inside `import.meta.env.DEV`, so Vite dead-code-
// eliminates the dynamic import and never emits this chunk in a prod build.
const ShellPreviewHarness = import.meta.env.DEV
  ? lazy(() => import("./components/shell/ShellPreviewHarness").then((m) => ({ default: m.ShellPreviewHarness })))
  : null;
const PoliciesPage = lazy(() => import("./components/policies/PoliciesPage").then((m) => ({ default: m.PoliciesPage })));
const MaterialsPage = lazy(() => import("./components/materials/MaterialsPage").then((m) => ({ default: m.MaterialsPage })));
const OutputPage = lazy(() => import("./components/output/OutputPage").then((m) => ({ default: m.OutputPage })));
const SettingsPage = lazy(() => import("./components/settings/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const HelpPage = lazy(() => import("./components/help/HelpPage").then((m) => ({ default: m.HelpPage })));
const NotFound = lazy(() => import("./components/common/NotFound").then((m) => ({ default: m.NotFound })));
const PageLayoutPage = lazy(() => import("./components/pageLayout/PageLayoutPage").then((m) => ({ default: m.PageLayoutPage })));
// Firmark staff console — code-split so it never ships in the normal customer
// bundle, and gated on user.isStaff (contract §7). Lifts to firmark.dev later.
// AdminConsole is the layout shell; each section is its own lazy chunk so a
// staff user only downloads the section they open.
const AdminConsole = lazy(() => import("./components/admin/AdminConsole").then((m) => ({ default: m.AdminConsole })));
const AdminOverview = lazy(() => import("./components/admin/sections/AdminOverview").then((m) => ({ default: m.AdminOverview })));
const AdminOrganizations = lazy(() => import("./components/admin/sections/AdminOrganizations").then((m) => ({ default: m.AdminOrganizations })));
const AdminOrgDetail = lazy(() => import("./components/admin/sections/AdminOrgDetail").then((m) => ({ default: m.AdminOrgDetail })));
const AdminUsers = lazy(() => import("./components/admin/sections/AdminUsers").then((m) => ({ default: m.AdminUsers })));
const AdminUserDetail = lazy(() => import("./components/admin/sections/AdminUserDetail").then((m) => ({ default: m.AdminUserDetail })));
const AdminInvites = lazy(() => import("./components/admin/sections/AdminInvites").then((m) => ({ default: m.AdminInvites })));
const AdminAudit = lazy(() => import("./components/admin/sections/AdminAudit").then((m) => ({ default: m.AdminAudit })));
const AdminBeta = lazy(() => import("./components/admin/sections/AdminBeta").then((m) => ({ default: m.AdminBeta })));

export function App() {
  return (
    <BrowserRouter
      future={{
        v7_startTransition: true,
        // v7_relativeSplatPath intentionally NOT enabled: the sidebar
        // navs in Policies/Settings/Materials use relative links inside
        // splat routes (to="design" from /app/policies/*), which the v7
        // resolution appends to the current leaf instead of the section
        // root, breaking every sub-nav. Convert those links to absolute
        // paths before enabling this flag.
      }}
    >
      <OrganizationProvider>
        <AuthProvider>
          <ToastProvider>
            <AppRoutes />
          </ToastProvider>
        </AuthProvider>
      </OrganizationProvider>
    </BrowserRouter>
  );
}

function AppRoutes() {
  return (
    <Routes>
      {/* ---- Public front door (no auth) ---- */}
      <Route path="/" element={<LandingPage />} />
      {/* Public marketing deep pages (hub + Platform/Security/Learn) */}
      <Route path="/platform/design-engine" element={<Suspense fallback={<LoadingState fullHeight />}><DesignEnginePage /></Suspense>} />
      <Route path="/platform/engineering" element={<Suspense fallback={<LoadingState fullHeight />}><EngineeringPage /></Suspense>} />
      <Route path="/plans" element={<Suspense fallback={<LoadingState fullHeight />}><SendPlanPage /></Suspense>} />
      <Route path="/security" element={<Suspense fallback={<LoadingState fullHeight />}><SecurityPage /></Suspense>} />
      <Route path="/learn" element={<Suspense fallback={<LoadingState fullHeight />}><LearnPage /></Suspense>} />
      <Route path="/learn/:slug" element={<Suspense fallback={<LoadingState fullHeight />}><LearnArticlePage /></Suspense>} />
      <Route path="/help" element={<Suspense fallback={<LoadingState fullHeight />}><PublicHelpPage /></Suspense>} />
      <Route path="/help/:tabId" element={<Suspense fallback={<LoadingState fullHeight />}><PublicHelpPage /></Suspense>} />
      <Route path="/help/:tabId/:articleId" element={<Suspense fallback={<LoadingState fullHeight />}><PublicHelpPage /></Suspense>} />
      <Route path="/privacy" element={<Suspense fallback={<LoadingState fullHeight />}><PrivacyPage /></Suspense>} />
      <Route path="/terms" element={<Suspense fallback={<LoadingState fullHeight />}><TermsPage /></Suspense>} />
      <Route path="/login" element={<PublicLoginPage />} />
      <Route path="/beta" element={<BetaSignupPage />} />
      {/* Invite-gated account setup. Reachable via /signup?invite=<token> from
          an approved beta request or a founder direct-invite; without a valid
          token it renders an "invite required" state. */}
      <Route path="/signup" element={<AccountSetupPage />} />
      <Route path="/forgot" element={<ForgotPasswordPage />} />
      <Route path="/reset" element={<ResetPasswordPage />} />
      {/* Short-lived, token-scoped report route consumed by the API's headless
          renderer. It intentionally has no app shell or user-session requirement. */}
      <Route path="/calc-export/:exportId" element={<Suspense fallback={<LoadingState fullHeight />}><CalcExportRenderPage /></Suspense>} />

      {/* ---- Authenticated app, mounted behind /app ----
          The whole app tree nests under one /app parent so its children
          stay RELATIVE (projects, policies/*, …). That's what keeps the
          inner Policies/Settings/Materials sub-navs (relative links inside
          splat routes) working with v7_relativeSplatPath off. */}
      <Route path="/app" element={<RequireAuth />}>
        <Route element={<Suspense fallback={<LoadingState fullHeight />}><AppShell /></Suspense>}>
          <Route index element={<ProjectDashboard />} />
          <Route path="projects" element={<ProjectDashboard />} />
          <Route path="projects/new" element={<ProjectCreate />} />
          <Route path="projects/:id/*" element={<ProjectDetail />} />
          {/* The calc LIST + create wizard stay inside the portal AppShell (they
              are project-management surfaces, not the full-bleed calc app). The
              per-PROJECT calc surfaces (viewer / catalog / report) are routed
              full-bleed below, outside AppShell. React Router v6 ranks the
              static `new` segment above the dynamic `:id`, so /new still hits
              CalcCreate even though :id is matched in the other group. */}
          <Route path="calculations" element={<CalcDashboard />} />
          <Route path="calculations/new" element={<CalcCreate />} />
          {/* Calc projects stay inside the real portal shell. Their own nested
              layout fills the shell's one sidebar slot with project/calculation
              navigation; the header can still open the portal nav as a drawer. */}
          <Route path="calculations/:id" element={<CalcProjectLayout />}>
            <Route index element={<Suspense fallback={<LoadingState fullHeight />}><CalcViewerShell /></Suspense>} />
            <Route path="add" element={<Suspense fallback={<LoadingState fullHeight />}><AddCalculationShell /></Suspense>} />
            <Route path="m/:sheetId" element={<Suspense fallback={<LoadingState fullHeight />}><NdsCalcWorkspace /></Suspense>} />
            <Route path="c/:sheetId" element={<Suspense fallback={<LoadingState fullHeight />}><SchemaCalcSheet /></Suspense>} />
            <Route path="l/:sheetId" element={<Suspense fallback={<LoadingState fullHeight />}><LoadCalcWorkspace /></Suspense>} />
            <Route path="load/:templateId" element={<Suspense fallback={<LoadingState fullHeight />}><LoadCalcWorkspace /></Suspense>} />
            <Route path="report-details" element={<Suspense fallback={<LoadingState fullHeight />}><CalcReportDetails /></Suspense>} />
          </Route>
          <Route path="policies/*" element={<PoliciesPage />} />
          <Route path="materials/*" element={<MaterialsPage />} />
          <Route path="output/page-layout" element={<PageLayoutPage />} />
          <Route path="output/page-layout/:templateId" element={<PageLayoutPage />} />
          <Route path="output/*" element={<OutputPage />} />
          <Route path="settings/*" element={<SettingsPage />} />
          <Route path="admin" element={<RequireStaff />}>
            <Route element={<AdminConsole />}>
              <Route index element={<Navigate to="overview" replace />} />
              <Route path="overview" element={<AdminOverview />} />
              <Route path="organizations" element={<AdminOrganizations />} />
              <Route path="organizations/:orgId" element={<AdminOrgDetail />} />
              <Route path="users" element={<AdminUsers />} />
              <Route path="users/:userId" element={<AdminUserDetail />} />
              <Route path="invites" element={<AdminInvites />} />
              <Route path="audit" element={<AdminAudit />} />
              <Route path="beta" element={<AdminBeta />} />
            </Route>
          </Route>
          <Route path="help" element={<HelpPage />} />
          <Route path="help/:tabId" element={<HelpPage />} />
          <Route path="help/:tabId/:articleId" element={<HelpPage />} />
          <Route path="*" element={<NotFound />} />
        </Route>
        {/* Full-bleed CAD review — OUTSIDE AppShell: it brings its own Firmark
            header and hamburger, and its left/right space belongs to the drawing
            and the wizard, not the portal sidebar. React Router ranks the static
            "review" segment above the sibling `projects/:id/*` splat. */}
        <Route
          path="projects/:id/review"
          element={<Suspense fallback={<LoadingState fullHeight />}><CadReviewPage /></Suspense>}
        />
      </Route>

      {/* Dev-only visual-QA harness for the calc surfaces — hardcoded mock data,
          no auth, no api. Guarded by import.meta.env.DEV so the route (and its
          chunk) is NEVER in the production bundle. Reached at
          /calc-preview?s=viewer|catalog|report|shell. */}
      {import.meta.env.DEV && CalcPreviewHarness && (
        <Route
          path="/calc-preview/*"
          element={<Suspense fallback={<LoadingState fullHeight />}><CalcPreviewHarness /></Suspense>}
        />
      )}
      {import.meta.env.DEV && CalcExportPreviewPage && (
        <Route path="/calc-export-preview/:calculatorId/:mode" element={<Suspense fallback={<LoadingState fullHeight />}><CalcExportPreviewPage /></Suspense>} />
      )}

      {/* Dev-only look at the CAD review chrome with an empty drawing — no auth,
          no api, no pipeline run. Guarded by import.meta.env.DEV so the route is
          NEVER in the production bundle. Reached at /cad-preview. */}
      {import.meta.env.DEV && CadReviewPreview && (
        <Route
          path="/cad-preview"
          element={<Suspense fallback={<LoadingState fullHeight />}><CadReviewPreview /></Suspense>}
        />
      )}

      {/* Dev-only visual-QA harness for the portal AppShell CHROME — mock
          auth/org, no api. Guarded by import.meta.env.DEV so the route (and its
          chunk) is NEVER in the production bundle. Reached at /portal-preview. */}
      {import.meta.env.DEV && ShellPreviewHarness && (
        <Route
          path="/portal-preview/*"
          element={<Suspense fallback={<LoadingState fullHeight />}><ShellPreviewHarness /></Suspense>}
        />
      )}

      {/* Unknown top-level paths fall back to the public landing. */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Guards every /app/* route. While the initial token check is in flight we
// hold on a loading state (so we don't flash a redirect to /login before the
// session resolves). Unauthenticated visits bounce to /login, preserving the
// intended destination in ?next= so the user lands back there after signing in.
function RequireAuth() {
  const { state } = useAuth();
  const location = useLocation();

  if (state.kind === "loading") {
    return <LoadingState fullHeight />;
  }

  if (state.kind === "unauthenticated") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }

  return <Outlet />;
}

// Gates the staff console. Sits inside /app (so the user is already authenticated);
// non-staff users are bounced to the dashboard. Backend enforces the same via
// require_staff on /api/admin/* (contract §7) — the client gate is convenience, not security.
function RequireStaff() {
  const { user } = useAuth();
  if (!user?.isStaff) return <Navigate to="/app" replace />;
  return <Outlet />;
}
